/**
 * Shared, pure view-model for the member-facing billing UI.
 *
 * The same function feeds My Retail, the Executive success page and the
 * regression tests, so the copy and the available action can never drift.
 */

import { type BillingSource, MANUAL_GRANT_DETAIL } from "@/lib/payments/billing-source";

export type BillingState =
  | "none"
  | "processing"
  | "active"
  | "trialing"
  | "past_due"
  | "unpaid"
  | "cancelling"
  | "expired"
  | "incomplete"
  | "granted";

export interface BillingSnapshot {
  /** Raw Stripe status, or null when the member never subscribed. */
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** True when a real Stripe customer exists for this account. */
  hasBillingAccount: boolean;
  /** Manual grants have no Stripe customer and no self-service billing. */
  billingSource?: BillingSource;
  /** True when checkout completed but the webhook has not landed yet. */
  pending?: boolean;
}

export interface BillingView {
  state: BillingState;
  planLabel: string;
  statusLabel: string;
  detail: string;
  /** Executive tools unlocked. */
  entitled: boolean;
  action: "subscribe" | "manage" | "update_payment" | "wait" | "none";
  actionLabel: string;
  /** Portal is only meaningful once Stripe knows this customer. */
  showPortal: boolean;
}

const ACTIVE_STATUSES = new Set(["active", "trialing"]);

const FREE_PLAN = "Free account";
const EXEC_PLAN = "Executive Membership";

function formatDate(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function billingView(snapshot: BillingSnapshot): BillingView {
  const end = formatDate(snapshot.currentPeriodEnd);
  const future =
    !snapshot.currentPeriodEnd || new Date(snapshot.currentPeriodEnd).getTime() > Date.now();

  if (snapshot.pending && !snapshot.status) {
    return {
      state: "processing",
      planLabel: EXEC_PLAN,
      statusLabel: "Payment processing",
      detail: "Your payment went through. We are activating your membership, this takes a few seconds.",
      entitled: false,
      action: "wait",
      actionLabel: "Activating…",
      showPortal: false,
    };
  }

  if (!snapshot.status) {
    return {
      state: "none",
      planLabel: FREE_PLAN,
      statusLabel: "No membership",
      detail: "Upgrade to unlock the Deal & Expansion Tracker, exports and the full Executive briefing.",
      entitled: false,
      action: "subscribe",
      actionLabel: "Become an Executive Member",
      showPortal: false,
    };
  }

  // A manual, permanently granted membership never touches Stripe: no portal,
  // no billing action, no expiry. Sentinel identifiers stay server-side.
  if (snapshot.billingSource === "manual" && ACTIVE_STATUSES.has(snapshot.status)) {
    return {
      state: "granted",
      planLabel: EXEC_PLAN,
      statusLabel: "Active",
      detail: MANUAL_GRANT_DETAIL,
      entitled: true,
      action: "none",
      actionLabel: "",
      showPortal: false,
    };
  }

  const base = {
    planLabel: EXEC_PLAN,
    showPortal: snapshot.hasBillingAccount && snapshot.billingSource !== "manual",
  };

  switch (snapshot.status) {
    case "trialing":
      return {
        ...base,
        state: "trialing",
        statusLabel: "Trial",
        detail: end ? `Your trial runs until ${end}.` : "Your trial is running.",
        entitled: future,
        action: "manage",
        actionLabel: "Manage billing",
      };
    case "active":
      if (snapshot.cancelAtPeriodEnd) {
        return {
          ...base,
          state: "cancelling",
          statusLabel: "Cancels at period end",
          detail: end
            ? `Your membership stays open until ${end} and will not renew.`
            : "Your membership will not renew.",
          entitled: future,
          action: "manage",
          actionLabel: "Manage billing",
        };
      }
      return {
        ...base,
        state: "active",
        statusLabel: "Active",
        detail: end ? `Renews on ${end}.` : "Your membership is active.",
        entitled: true,
        action: "manage",
        actionLabel: "Manage billing",
      };
    case "past_due":
      return {
        ...base,
        state: "past_due",
        statusLabel: "Payment failed",
        detail:
          "We could not take your last payment. Update your payment method to keep your membership open.",
        entitled: future,
        action: "update_payment",
        actionLabel: "Update payment method",
      };
    case "unpaid":
      return {
        ...base,
        state: "unpaid",
        statusLabel: "Unpaid",
        detail: "Your membership is suspended after repeated failed payments. Update your card to restore access.",
        entitled: false,
        action: "update_payment",
        actionLabel: "Update payment method",
      };
    case "incomplete":
      return {
        ...base,
        state: "incomplete",
        statusLabel: "Awaiting payment",
        detail: "Your first payment has not completed yet. Finish it to activate your membership.",
        entitled: false,
        action: "update_payment",
        actionLabel: "Complete payment",
      };
    case "canceled":
      if (future && snapshot.currentPeriodEnd) {
        return {
          ...base,
          state: "cancelling",
          statusLabel: "Cancelled, access until period end",
          detail: `Your membership is cancelled and stays open until ${end}.`,
          entitled: true,
          action: "subscribe",
          actionLabel: "Become an Executive Member",
        };
      }
      return {
        ...base,
        state: "expired",
        statusLabel: "Expired",
        detail: end ? `Your membership ended on ${end}.` : "Your membership has ended.",
        entitled: false,
        action: "subscribe",
        actionLabel: "Become an Executive Member",
      };
    default:
      return {
        ...base,
        state: "expired",
        statusLabel: "Inactive",
        detail: "Your membership is not active.",
        entitled: false,
        action: "subscribe",
        actionLabel: "Become an Executive Member",
      };
  }
}
